import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Page,
  Card,
  Button,
  BlockStack,
  InlineStack,
  Text,
  Divider,
  Badge,
  Banner
} from '@shopify/polaris';
import axios from 'axios';
import { AppContext } from '../App';

function BillingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { shop, setShop, showToast, API } = useContext(AppContext);
  const [plans, setPlans] = useState([]);
  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const shopParam = searchParams.get('shop') || localStorage.getItem('shop');
    if (shopParam) {
      setShop(shopParam);
      fetchPlans();
      fetchBillingStatus(shopParam);
    } else {
      navigate('/');
    }
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/billing/plans`);
      setPlans(response.data.plans || []);
    } catch (err) {
      showToast('Failed to fetch plans', true);
    }
  };

  const fetchBillingStatus = async (shopDomain) => {
    try {
      const response = await axios.get(`${API}/billing/status`, {
        params: { shop: shopDomain }
      });
      setBillingStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch billing status');
    }
  };

  const handleSubscribe = async (planId) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/billing/subscribe`,
        null,
        {
          params: {
            shop: shop,
            plan_id: planId
          }
        }
      );

      if (response.data.status === 'redirect') {
        window.top.location.href = response.data.confirmation_url;
      } else {
        showToast('Plan activated!');
        fetchBillingStatus(shop);
      }
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to subscribe', true);
    } finally {
      setLoading(false);
    }
  };

  const getTrialDaysRemaining = () => {
    if (!billingStatus || !billingStatus.trial_ends_at) return 0;
    const now = new Date();
    const trialEnd = new Date(billingStatus.trial_ends_at);
    const diff = trialEnd - now;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  return (
    <Page
      title="Billing"
      backAction={{ onAction: () => navigate(`/orders?shop=${shop}`) }}
    >
      <BlockStack gap="400">
        {billingStatus && billingStatus.plan === 'trial' && (
          <Banner tone="info" data-testid="trial-banner">
            <p>You have {getTrialDaysRemaining()} days remaining in your trial. Upgrade to Pro to continue using automation features.</p>
          </Banner>
        )}

        {billingStatus && billingStatus.plan === 'trial_expired' && (
          <Banner tone="warning" data-testid="trial-expired-banner">
            <p>Your trial has expired. Please select a plan to continue.</p>
          </Banner>
        )}

        {billingStatus && billingStatus.plan === 'pro' && (
          <Banner tone="success" data-testid="active-plan-banner">
            <p>You are on the Pro Plan. All features are enabled.</p>
          </Banner>
        )}

        {plans.map((plan) => (
          <Card key={plan.id}>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <BlockStack gap="100">
                  <InlineStack gap="200" align="center">
                    <Text variant="headingLg" as="h2">{plan.name}</Text>
                    {billingStatus && billingStatus.plan === plan.id && (
                      <Badge tone="success">Current Plan</Badge>
                    )}
                    {billingStatus && billingStatus.plan === 'trial' && plan.id === 'pro' && (
                      <Badge tone="info">Trial Active</Badge>
                    )}
                  </InlineStack>
                  <Text variant="headingMd" as="p">
                    {plan.price === 0 ? 'Free' : `$${plan.price} AUD/month`}
                  </Text>
                </BlockStack>
              </InlineStack>
              <Divider />
              <BlockStack gap="200">
                {plan.features.map((feature, idx) => (
                  <Text key={idx} variant="bodyMd" as="p">✓ {feature}</Text>
                ))}
              </BlockStack>
              {billingStatus && billingStatus.plan !== plan.id && (
                <Button
                  primary={plan.id === 'pro'}
                  onClick={() => handleSubscribe(plan.id)}
                  loading={loading}
                  data-testid={`subscribe-${plan.id}-btn`}
                >
                  {plan.id === 'pro' ? 'Start 14-Day Trial' : 'Select Plan'}
                </Button>
              )}
            </BlockStack>
          </Card>
        ))}

        <Card>
          <BlockStack gap="200">
            <Text variant="headingMd" as="h2">Billing FAQ</Text>
            <Divider />
            <Text variant="bodyMd" as="p" fontWeight="semibold">When will I be charged?</Text>
            <Text variant="bodyMd" as="p" tone="subdued">
              You will not be charged during your 14-day trial. After the trial, you'll be prompted to confirm your subscription.
            </Text>
            <Text variant="bodyMd" as="p" fontWeight="semibold">What happens if payment fails?</Text>
            <Text variant="bodyMd" as="p" tone="subdued">
              Automation features will be disabled, but you can still create labels manually.
            </Text>
            <Text variant="bodyMd" as="p" fontWeight="semibold">Can I cancel anytime?</Text>
            <Text variant="bodyMd" as="p" tone="subdued">
              Yes, you can downgrade to the Starter plan at any time.
            </Text>
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}

export default BillingPage;